import {
  Alert,
  Badge,
  Box,
  Button,
  Heading,
  HStack,
  Icon,
  Table,
  Text,
} from '@chakra-ui/react'
import { Music } from 'lucide-react'
import React, { useState } from 'react'
import { Link } from 'react-router'

import type { ToastLike } from '@mangovoice/mango-ui'
import { ErrorState, LoadingState } from '@mangovoice/mango-ui'

import type {
  MusicOnHoldApi,
  Playlist,
  PlaylistListItem,
  UpdatePlaylistPayload,
} from '../api/musicOnHoldApi.types'
import { DetailRow, DetailSection } from '../components/DetailSection'
import { EditPlaylistModal } from '../components/EditPlaylistModal'
import { ManageTracksModal } from '../components/ManageTracksModal'
import { TrackPlayButton } from '../components/TrackPlayButton'
import {
  useGetPlaylist,
  useGetPlaylists,
  useUpdatePlaylist,
} from '../queries/musicOnHoldQueries'
import { type MusicOnHoldTranslateFn, text } from '../types/i18n.types'

const formatDateTime = (dateString?: string): string => {
  if (!dateString) return '—'
  try {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return '—'
  }
}

export interface PlaylistDetailPageProps {
  api: MusicOnHoldApi
  pbxId: number
  playlistId: number
  listPath?: string
  t?: MusicOnHoldTranslateFn
  toast?: ToastLike
}

export const PlaylistDetailPage: React.FC<PlaylistDetailPageProps> = ({
  api,
  pbxId,
  playlistId,
  listPath,
  t,
  toast,
}) => {
  const playlistQuery = useGetPlaylist(api, playlistId)
  const listFallback = useGetPlaylists(api, pbxId)
  const updateMutation = useUpdatePlaylist(api, pbxId)

  const [editOpen, setEditOpen] = useState(false)
  const [manageTracksOpen, setManageTracksOpen] = useState(false)

  const isLoading =
    playlistQuery.isLoading || (listFallback.isLoading && !playlistQuery.data)

  let playlist: (PlaylistListItem & Partial<Playlist>) | undefined

  if (playlistQuery.data) {
    playlist = playlistQuery.data
  } else if (listFallback.data) {
    const match = listFallback.data.find((p) => p.id === playlistId)
    if (match) playlist = match as PlaylistListItem & Partial<Playlist>
  }

  // Only show error if BOTH the detail and list queries failed
  const fatalError =
    !playlist && listFallback.error && !listFallback.data
      ? listFallback.error
      : null

  if (isLoading) {
    return (
      <LoadingState
        layout="alert"
        title={text(t, 'musicOnHold.detail.loading', 'Loading playlist...')}
      />
    )
  }

  if (fatalError) {
    return (
      <ErrorState
        layout="alert"
        title={text(t, 'musicOnHold.detail.error', 'Error')}
        description={(fatalError as Error).message}
      />
    )
  }

  if (!playlist) {
    return (
      <Alert.Root status="warning" p={4}>
        <Alert.Indicator />
        <Text>
          {text(t, 'musicOnHold.detail.notFound', 'Playlist not found.')}
        </Text>
      </Alert.Root>
    )
  }

  const isSystem = !!playlist.system && playlist.system > 0
  const hasType = !!playlist.type
  const audioTracks = playlist.audio_tracks ?? []

  const handleSave = (payload: UpdatePlaylistPayload) => {
    updateMutation.mutate(
      { id: playlistId, payload },
      {
        onSuccess: () => setEditOpen(false),
        onError: (err) => {
          toast?.create({
            title: text(
              t,
              'musicOnHold.editModal.error',
              'Failed to save playlist',
            ),
            description: err instanceof Error ? err.message : undefined,
            type: 'error',
          })
        },
      },
    )
  }

  return (
    <Box flex="1" w="full" p={6} overflow="auto">
      <HStack
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={4}
        mb={6}
      >
        <HStack gap={3} alignItems="center">
          {listPath && (
            <Link to={listPath} style={{ textDecoration: 'none' }}>
              <Text
                fontSize="sm"
                color="fg.muted"
                _hover={{ color: 'fg', textDecoration: 'underline' }}
              >
                {text(t, 'musicOnHold.detail.backToList', '← Back to list')}
              </Text>
            </Link>
          )}
          <Heading fontSize="2xl" mb={0}>
            {playlist.name}
          </Heading>
          {playlist.is_default && (
            <Badge colorPalette="info">
              {text(t, 'musicOnHold.detail.defaultBadge', 'Default')}
            </Badge>
          )}
          {isSystem && (
            <Badge colorPalette="neutral" variant="subtle">
              {text(t, 'musicOnHold.detail.systemBadge', 'System')}
            </Badge>
          )}
        </HStack>
        {!isSystem && (
          <Button
            colorPalette="brand"
            bg="brand.solid"
            color="brand.contrast"
            borderRadius="lg"
            onClick={() => setEditOpen(true)}
          >
            {text(t, 'musicOnHold.detail.editButton', 'Edit Playlist')}
          </Button>
        )}
      </HStack>

      <HStack align="start" gap={6} flexWrap="wrap">
        <Box flex="1" minW="300px">
          <DetailSection
            title={text(
              t,
              'musicOnHold.detail.generalTitle',
              'General Information',
            )}
          >
            <DetailRow label={text(t, 'musicOnHold.detail.nameLabel', 'Name')}>
              {playlist.name}
            </DetailRow>
            {hasType && (
              <DetailRow
                label={text(t, 'musicOnHold.detail.typeLabel', 'Type')}
              >
                <HStack gap={1} justify="flex-end">
                  <Icon asChild color="fg.muted" boxSize={3.5}>
                    <Music size={14} />
                  </Icon>
                  <Text>{text(t, 'musicOnHold.detail.typeList', 'List')}</Text>
                </HStack>
              </DetailRow>
            )}
            <DetailRow
              label={text(t, 'musicOnHold.detail.defaultLabel', 'Default')}
            >
              {playlist.is_default ? (
                <Badge colorPalette="info">
                  {text(t, 'musicOnHold.detail.defaultYes', 'Yes')}
                </Badge>
              ) : (
                <Text color="fg.muted">
                  {text(t, 'musicOnHold.detail.defaultNo', 'No')}
                </Text>
              )}
            </DetailRow>
            {playlist.active !== undefined && (
              <DetailRow
                label={text(t, 'musicOnHold.detail.activeLabel', 'Active')}
              >
                {playlist.active ? (
                  <Badge colorPalette="success">
                    {text(t, 'musicOnHold.detail.activeYes', 'Yes')}
                  </Badge>
                ) : (
                  <Badge colorPalette="neutral">
                    {text(t, 'musicOnHold.detail.activeNo', 'No')}
                  </Badge>
                )}
              </DetailRow>
            )}
            <DetailRow label={text(t, 'musicOnHold.detail.idLabel', 'ID')}>
              {playlist.id}
            </DetailRow>
            {playlist.uuid && (
              <DetailRow
                label={text(t, 'musicOnHold.detail.uuidLabel', 'UUID')}
              >
                <Text fontFamily="mono" fontSize="xs" color="fg.muted">
                  {playlist.uuid}
                </Text>
              </DetailRow>
            )}
            {playlist.created_at && (
              <DetailRow
                label={text(t, 'musicOnHold.detail.createdLabel', 'Created')}
              >
                {formatDateTime(playlist.created_at)}
              </DetailRow>
            )}
            {playlist.updated_at && (
              <DetailRow
                label={text(t, 'musicOnHold.detail.updatedLabel', 'Updated')}
              >
                {formatDateTime(playlist.updated_at)}
              </DetailRow>
            )}
          </DetailSection>
        </Box>

        <Box flex="1" minW="300px">
          <DetailSection
            title={`${text(t, 'musicOnHold.detail.audioTracksTitle', 'Audio Tracks')} (${audioTracks.length})`}
            headerAction={
              !isSystem && (
                <Button
                  size="xs"
                  variant="outline"
                  borderRadius="lg"
                  onClick={() => setManageTracksOpen(true)}
                >
                  {text(
                    t,
                    'musicOnHold.detail.manageTracksButton',
                    'Manage Tracks',
                  )}
                </Button>
              )
            }
          >
            {audioTracks.length > 0 ? (
              audioTracks.map((track) => (
                <DetailRow key={track.id} label={track.name}>
                  <HStack justify="flex-end" gap={2}>
                    <Text color="fg.muted" fontSize="xs">
                      {track.mp3_file_name}
                    </Text>
                    <TrackPlayButton
                      api={api}
                      pbxId={pbxId}
                      trackUuid={track.uuid}
                      t={t}
                    />
                  </HStack>
                </DetailRow>
              ))
            ) : (
              <Table.Row>
                <Table.Cell colSpan={2} py={4}>
                  <Text
                    color="fg.muted"
                    fontStyle="italic"
                    textAlign="center"
                    fontSize="sm"
                  >
                    {text(
                      t,
                      'musicOnHold.detail.noAudioTracks',
                      'There are no audio tracks associated with this playlist.',
                    )}
                  </Text>
                </Table.Cell>
              </Table.Row>
            )}
          </DetailSection>
        </Box>
      </HStack>

      {!isSystem && (
        <EditPlaylistModal
          open={editOpen}
          playlist={{ name: playlist.name }}
          onClose={() => setEditOpen(false)}
          onSave={handleSave}
          isSaving={updateMutation.isPending}
          t={t}
        />
      )}

      {!isSystem && (
        <ManageTracksModal
          api={api}
          pbxId={pbxId}
          playlistId={playlistId}
          open={manageTracksOpen}
          onClose={() => setManageTracksOpen(false)}
          t={t}
          toast={toast}
        />
      )}
    </Box>
  )
}
